import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiService } from "../utils/api";
import logo from "../assets/loginlogo.jpg";
import "./login.css";
import { initializeApp } from "firebase/app";
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { FaGoogle } from "react-icons/fa";

// Firebase config
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

// Helper for role-based redirection
const redirectToDashboard = (user) => {
  if (user.role === "admin") return "/admin";
  if (user.role === "teacher") return "/dashboard/teacher";
  if (user.role === "student") {
    return user.profileStatus === "pending" ? "/profile" : "/dashboard/student";
  }
  return "/login";
};

function Login({ setIsAuthenticated, setUser }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [username, setUsername] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTeacherMode, setIsTeacherMode] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const role = params.get("role");
    setIsTeacherMode(role === "teacher");

    // Load Google Sign-In if not already loaded
    if (!window.google) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = initGoogleSignIn;
      document.body.appendChild(script);
    } else {
      initGoogleSignIn();
    }
  }, [location]);

  const initGoogleSignIn = () => {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback,
      });

      window.google.accounts.id.renderButton(document.getElementById("google-signin-button"), { theme: "outline", size: "large", width: "100%" });
    }
  };

  const handleGoogleCallback = async (response) => {
    try {
      const googleResponse = await apiService.googleLogin({
        token: response.credential,
        isTeacher: isTeacherMode,
      });

      const data = googleResponse.data;

      if (data.success) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        setIsAuthenticated(true);
        setUser(data.user);
        navigate(redirectToDashboard(data.user));
      } else {
        setError(data.message || "Google login failed");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Error connecting to server");
    }
  };

  const handleFirebaseLogin = async (provider) => {
    try {
      setIsLoading(true);
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await user.getIdToken();

      const response = await apiService.firebaseLogin({
        token: idToken,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        role: isTeacherMode ? "teacher" : "student",
      });

      const data = response.data;
      if (data.success) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        setIsAuthenticated(true);
        setUser(data.user);
        navigate(redirectToDashboard(data.user));
      } else {
        setError(data.message || "Firebase login failed");
      }
    } catch (err) {
      console.error("Firebase login error:", err);
      setError(err.message || "Error connecting to Firebase");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      let response;
      if (isRegistering) {
        if (password !== confirmPassword) {
          setError("Passwords do not match");
          return;
        }
        if (password.length < 6) {
          setError("Password must be at least 6 characters");
          return;
        }

        response = await apiService.register({
          username,
          email,
          password,
          role: isTeacherMode ? "teacher" : "student",
        });
      } else {
        response = await apiService.login({
          email,
          password,
          role: isTeacherMode ? "teacher" : "student",
        });
      }

      const data = response.data;
      if (data.success) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        setIsAuthenticated(true);
        setUser(data.user);
        navigate(redirectToDashboard(data.user));
      } else {
        setError(data.message);
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Login error");
    }
  };

  const toggleMode = () => {
    setIsRegistering(!isRegistering);
    setError("");
  };

  const handleTeacherLogin = () => {
    setIsTeacherMode(!isTeacherMode);
    navigate(isTeacherMode ? "/login" : "/login?role=teacher");
  };

  return (
    <div className="login-container">
      <div className="login-left-panel">
        <img src={logo} alt="Institute Logo" className="login-logo" />
        <h2 className="institute-name">PITMANS SHORTHAND INSTITUTE</h2>
        <p className="institute-established">Established 1994</p>
      </div>

      <div className="login-right-panel">
        <div className="login-form-container">
          <h2 className="login-title">
            {isTeacherMode ? "Teacher" : "Student"} {isRegistering ? "Registration" : "Sign in"}
          </h2>
          <p className="login-subtitle">{isRegistering ? "Create your account" : `Access your ${isTeacherMode ? "teacher" : "student"} account`}</p>

          {!isTeacherMode && (
            <div className="social-login-buttons">
              <button onClick={() => handleFirebaseLogin(googleProvider)} className="social-login-button google-button" disabled={isLoading}>
                <FaGoogle className="social-icon" />
                <span>Continue with Google</span>
              </button>
            </div>
          )}

          {error && <p className="error-message">{error}</p>}

          <form onSubmit={handleSubmit} className="login-form">
            {isRegistering && (
              <div className="form-group">
                <label>Username</label>
                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Your name" required />
              </div>
            )}

            {(isTeacherMode || isRegistering) && (
              <>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" required />
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="******" required />
                </div>
              </>
            )}

            {isRegistering && (
              <div className="form-group">
                <label>Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="******" required />
              </div>
            )}

            <button type="submit" className="signin-button">
              <span className="signin-icon">→</span>
              {isTeacherMode ? "Teacher" : "Student"} {isRegistering ? "Register" : "Sign In"}
            </button>
          </form>

          <div className="login-footer">
            {!isRegistering && (
              <a href="#" className="forgot-password">
                Forgot password?
              </a>
            )}
            <div className="teacher-login">
              Are you a {isTeacherMode ? "student" : "teacher"}?
              <span onClick={handleTeacherLogin} className="teacher-login-link">
                {isTeacherMode ? "Student" : "Teacher"} Login
              </span>
            </div>
          </div>

          <p className="terms-text">
            By {isRegistering ? "registering" : "signing in"}, you agree to our <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
